<script setup lang="ts">
import { computed } from 'vue'
import discountPaymentImage from '~/assets/Discount-modal-img2.png'
import discountProductImage from '~/assets/Discount-modal-img.png'
import discountProducton from '~/assets/Discount-modal-on.png'

interface Props {
  modelValue?: boolean
  closeOnClickModal?: boolean
  beginnerLabel?: string
  guideLabel?: string
  title?: string
  subtitle?: string
  originalPriceLabel?: string
  orderPriceLabel?: string
  confirmText?: string
  closeAriaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  closeOnClickModal: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'close': []
  'confirm': []
  'guide': []
}>()

const { t } = useI18n()

const content = computed(() => ({
  beginnerLabel: props.beginnerLabel ?? t('discountModal.beginnerLabel'),
  guideLabel: props.guideLabel ?? t('discountModal.guideLabel'),
  title: props.title ?? t('discountModal.title'),
  subtitle: props.subtitle ?? t('discountModal.subtitle'),
  originalPriceLabel: props.originalPriceLabel ?? t('discountModal.originalPriceLabel'),
  orderPriceLabel: props.orderPriceLabel ?? t('discountModal.orderPriceLabel'),
  confirmText: props.confirmText ?? t('discountModal.confirmText'),
  closeAriaLabel: props.closeAriaLabel ?? t('discountModal.closeAriaLabel'),
}))

const visible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

function close() {
  visible.value = false
}

function handleClosed() {
  emit('close')
}

function handleConfirm() {
  emit('confirm')
  close()
}

function handleGuide() {
  window.location.href = '/v?url=news_guide'
}
</script>

<template>
  <el-dialog
    v-model="visible"
    append-to-body
    align-center
    class="discount-modal"
    modal-class="discount-modal-overlay"
    width="684px"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="true"
    :show-close="false"
    @closed="handleClosed"
  >
    <div class="discount-modal__content">
      <header class="discount-modal__hero">
        <div class="discount-modal__hero-left">
          <div class="discount-modal__beginner">
            <span class="discount-modal__book-icon" aria-hidden="true">
              <i />
            </span>
            <span>{{ content.beginnerLabel }}</span>
          </div>
          <div class="discount-modal__copy">
            <h2>{{ content.title }}</h2>
            <p>{{ content.subtitle }}</p>
          </div>
        </div>

        <button class="discount-modal__close" type="button" :aria-label="content.closeAriaLabel" @click="close">
          <span aria-hidden="true" />
        </button>

        <button class="discount-modal__guide" type="button" @click="handleGuide">
          {{ content.guideLabel }}
        </button>
      </header>

      <div class="discount-modal__comparison">
        <section class="discount-modal__card">
          <h3>{{ content.originalPriceLabel }}</h3>
          <img
            :src="discountProductImage"
            :alt="content.originalPriceLabel"
            width="239"
            height="258"
          >
        </section>

        <img :src="discountProducton" alt="" class="discount-modal__arrow">

        <section class="discount-modal__card">
          <h3 class="discount-modal__card-title--accent">
            {{ content.orderPriceLabel }}
          </h3>
          <img
            :src="discountPaymentImage"
            :alt="content.orderPriceLabel"
            width="238"
            height="257"
          >
        </section>
      </div>

      <button class="discount-modal__confirm" type="button" @click="handleConfirm">
        {{ content.confirmText }}
      </button>
    </div>
  </el-dialog>
</template>

<style scoped lang="less">
@brand-primary: #ff7228;
@text-primary: #2b211c;
@border-color: #ffe1ce;

:global(.discount-modal.el-dialog) {
  max-width: calc(100vw - 32px);
  padding: 0;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
}

:global(.discount-modal .el-dialog__header) {
  display: none;
}

:global(.discount-modal .el-dialog__body) {
  padding: 0;
}

:global(.discount-modal-overlay) {
  background: rgba(26, 24, 23, 0.6);
}

.discount-modal__content {
  box-sizing: border-box;
  display: flex;
  width: 684px;
  max-width: 100%;
  padding: 24px 32px;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  background: linear-gradient(180deg, var(--promotion-default, #FF4600) 0%, var(--red-orange-300, #FFB06D) 49.37%, rgba(255, 255, 255, 0.00) 98.75%);
  background-size: 100% 340px;
  background-repeat: no-repeat;
}

.discount-modal__hero {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 120px;
  flex: none;
  overflow: hidden;
  color: #fff;
  display: flex;
  align-items: center;
  .discount-modal__hero-left{
    flex:1;
    padding-right: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
}

.discount-modal__close
 {
  position: absolute;
  z-index: 1;
}

.discount-modal__beginner {
  top: 12px;
  left: 12px;
  display: inline-flex;
  height: 24px;
  padding: 0 10px 0 8px;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
}

.discount-modal__book-icon {
  position: relative;
  display: inline-block;
  box-sizing: border-box;
  width: 13px;
  height: 12px;
  border: 1.5px solid currentcolor;
  border-radius: 2px;

  &::after {
    position: absolute;
    top: -1px;
    bottom: -1px;
    left: 50%;
    width: 1px;
    background: currentcolor;
    content: '';
  }

  i {
    position: absolute;
    top: -3px;
    left: 5px;
    width: 3px;
    height: 3px;
    border: 1px solid currentcolor;
    border-bottom: 0;
    border-radius: 2px 2px 0 0;
  }
}

.discount-modal__guide {
  top: 16px;
  right: 28px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.92);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 34px;
  transition: background-color 0.15s ease;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.28);
  }
}

.discount-modal__close {
  top: 0;
  right: 0;
  display: grid;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  place-items: center;

  &::before,
  span {
    position: absolute;
    width: 21px;
    height: 2px;
    border-radius: 1px;
    background: currentcolor;
    content: '';
    transform: rotate(45deg);
  }

  span {
    transform: rotate(-45deg);
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid #fff;
    outline-offset: -2px;
  }
}

.discount-modal__copy {
  margin-top: 16px;
  h2,
  p {
    margin: 0;
  }

  h2 {
    font-size: 28px;
    font-weight: 700;
    line-height: 36px;
  }

  p {
    color: rgba(255, 255, 255, 0.92);
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
}

.discount-modal__comparison {
  position: relative;
  display: grid;
  width: 100%;
  height: 306px;
  grid-template-columns: repeat(2, 302px);
  gap: 16px;
}

.discount-modal__card {
  position: relative;
  box-sizing: border-box;
  display: flex;
  width: 302px;
  align-items: center;
  justify-content: flex-end;
  overflow: hidden;
  border: 1px solid @border-color;
  border-radius: 16px;
  background: linear-gradient(0deg, #FFF 43.14%, var(--red-orange-100, #FFEAD3) 100%);
  flex-direction: column;

  h3 {
    height: 46px;
    margin: 0;
    padding: 0 20px;
    border-radius: 0 0 14px 14px;
    background: #fff7ef;
    color: @text-primary;
    font-size: 18px;
    font-weight: 700;
    line-height: 46px;
    text-align: center;
    white-space: nowrap;
    margin-bottom: 8.26px;

  }

  .discount-modal__card-title--accent {
    background: @brand-primary;
    color: #fff;
  }

  img {
    display: block;
    max-width: calc(100% - 32px);
    height: 258px;
    object-fit: contain;
  }
}

.discount-modal__arrow {
  position: absolute;
  z-index: 2;
  top: 134px;
  left: 50%;
  width: 44px;
  height: 38px;
  transform: translateX(-50%);
}

.discount-modal__confirm {
  box-sizing: border-box;
  min-width: 200px;
  height: 40px;
  padding: 10px 60px;
  border: 0;
  border-radius: 4px;
  background: @brand-primary;
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  transition: background-color 0.15s ease;

  &:hover,
  &:focus-visible {
    background: #fe5911;
  }

  &:active {
    background: #ef3e07;
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 114, 40, 0.35);
    outline-offset: 2px;
  }
}

@media (max-width: 720px) {
  .discount-modal__content {
    padding: 20px;
  }

  .discount-modal__comparison {
    height: auto;
    grid-template-columns: minmax(0, 1fr);
  }

  .discount-modal__card {
    width: 100%;
  }

  .discount-modal__arrow {
    display: none;
  }
}
</style>
